import { IconButton, Tooltip } from "@chakra-ui/react";
import { RiExternalLinkLine } from "react-icons/ri";
import { useOrganization } from "src/hooks/useOrganization";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";

type PersonProfileLinkProps = {
  personId: string | undefined;
  personType: GQL.person_type_enum | null;
  /** Optional: show "view" path instead of "edit" path */
  viewMode?: boolean;
  /** Chakra IconButton size; default `sm`. */
  buttonSize?: "xs" | "sm" | "md" | "lg";
};

/**
 * Renders an icon button that navigates to a person's profile page based on admin permissions.
 *
 * Access Control:
 * - ADMIN, ORG_ADMIN, DISTRICT_ADMIN: Can view profile icon button
 * - SCHOOL_ADMIN and below: Cannot view profiles (no button rendered)
 *
 * Note: This is a UX convenience layer. Actual access control is enforced
 * server-side via Hasura GraphQL permissions.
 */
export const PersonProfileLink: React.FC<PersonProfileLinkProps> = ({
  personId,
  personType,
  viewMode = false,
  buttonSize = "sm",
}) => {
  const organization = useOrganization();

  // Check if user has permission to view profile pages
  const canViewProfiles = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
    HasuraRole.DISTRICT_ADMIN,
  ]);

  // If no permission or missing data, don't render button
  if (!canViewProfiles || !personId || !personType) {
    return null;
  }

  // Determine the correct route type based on person type
  const routeType =
    personType === GQL.person_type_enum.applicant
      ? Url.OrgAdmin.Students
      : personType === GQL.person_type_enum.guardian
      ? Url.OrgAdmin.Parents
      : null;

  // If no valid route type, don't render button
  if (!routeType) {
    return null;
  }

  // Build the URL based on view mode
  const url = viewMode
    ? routeType.view(organization, personId)
    : routeType.edit(organization, personId);

  // Create accessible label based on person type and mode
  const personTypeLabel =
    personType === GQL.person_type_enum.applicant
      ? "student"
      : personType === GQL.person_type_enum.guardian
      ? "parent"
      : "person";
  const tooltipLabel = `${
    viewMode ? "View" : "Edit"
  } ${personTypeLabel} profile`;

  const handleClick = () => {
    organization.do((org) => {
      const fullUrl = url.replace(`:${org.path}`, org.path);
      window.open(fullUrl, "_blank", "noopener,noreferrer");
    });
  };

  return (
    <Tooltip label={tooltipLabel}>
      <IconButton
        aria-label={tooltipLabel}
        icon={<RiExternalLinkLine />}
        size={buttonSize}
        variant="ghost"
        onClick={handleClick}
      />
    </Tooltip>
  );
};
